<template lang="pug">
div._root
    router-view

Footer

Teleport(to="body")
    TransitionGroup.bq-toast-container(name="bq-toast" tag="div")
        div.bq-toast(v-for="t in toasts" :key="t.id" :class="`bq-toast--${t.type}`")
            span.bq-toast__msg {{ t.message }}#[a.bq-toast__link(v-if="t.link" :href="t.link.href" target="_blank" rel="noopener") {{ t.link.label }}]
            button.bq-toast__close(type="button" @click="dismissToast(t.id)" aria-label="Dismiss") ×
</template>

<script setup>
import Footer from "@/components/footer.vue";
import { toasts, dismissToast } from "@/components/toast";
import { initKeyboardInset } from "@/code/keyboard-inset";

initKeyboardInset();
</script>
<style lang="less">
@import "@/assets/less/bq-tokens.less";

/* ─── sticky footer ───────────────────────────────────────────────────────
   The footer has to sit at the bottom of the VIEWPORT when a page is shorter
   than the screen, and directly after the content when it is taller.

   This used to be done by pushing ._root down with
   `min-height: calc(99vh - var(--footer-height, 0))`, which failed twice:

     - the fallback was a unitless `0`, and `calc(<length> - <number>)` is
       invalid, so the declaration was thrown away entirely until footer.vue's
       onMounted wrote --footer-height in px. Every page therefore rendered
       with NO min-height for the first frames, which is why a short page that
       was still loading showed the footer part-way up the screen.
     - the height was measured once on mount, so it went stale as soon as the
       footer rewrapped (window resize, a route with a taller footer).

   Flexbox needs neither a magic viewport number nor a measurement: ._root
   absorbs all the leftover height, so the footer is pinned to the bottom no
   matter how tall it is or how little content the page has. --footer-height is
   still published by footer.vue because agent.vue sizes the chat box with it,
   but no layout depends on it any more. */
#app {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
}

/* dvh tracks the collapsing mobile URL bar. It has to live in its own
   @supports block, not as a second min-height in the rule above: the CSS
   minifier collapses two declarations of the same property in one rule down to
   the last, which silently threw the 100vh fallback away. */
@supports (min-height: 100dvh) {
    #app {
        min-height: 100dvh;
    }
}

/* On-screen keyboard up (code/keyboard-inset.ts). Where the keyboard covers
   the page instead of resizing it, the last fields of a form sit behind the
   keyboard with no page left to scroll. The covered height is added as
   scroll room, so any field can be brought above the keyboard. */
html.kb-open #app {
    padding-bottom: var(--kb, 0px);
}

#app > ._root {
    /* Grow into the spare space, never shrink below the content. */
    flex: 1 0 auto;
}

#app > #footer {
    flex: 0 0 auto;
}

.bq-toast-container {
    position: fixed;
    bottom: 1.5rem;
    right: 1.5rem;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-width: min(28rem, calc(100vw - 2rem));
    pointer-events: none;
}

/* Above the on-screen keyboard, not behind it. */
html.kb-open .bq-toast-container {
    bottom: calc(1.5rem + var(--kb, 0px));
}

.bq-toast {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    border: 1px solid;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    pointer-events: all;
    line-height: 1.4;
    font-size: 0.875rem;

    &--error   { background: @danger-bg;  border-color: @danger;   color: @danger;   }
    &--warning { background: @warning-bg; border-color: @warning-border; color: @warning; }
    &--info    { background: @info-bg;    border-color: @info-border;    color: @info;    }
}

.bq-toast__msg {
    flex: 1;
    color: inherit;
    word-break: break-word;
}

/* The docs link a toast may carry: same colour as the sentence, underlined. */
.bq-toast__link {
    margin-left: 0.35rem;
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
    white-space: nowrap;
}

.bq-toast__close {
    flex-shrink: 0;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    font-size: 1.1rem;
    line-height: 1;
    color: inherit;
    opacity: 0.6;
    &:hover { opacity: 1; }
}

.bq-toast-enter-active,
.bq-toast-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}
.bq-toast-enter-from { opacity: 0; transform: translateY(0.5rem); }
.bq-toast-leave-to   { opacity: 0; transform: translateY(0.5rem); }


.service-swiper {

    .swiper-pagination-fraction,
    .swiper-pagination-custom,
    .swiper-horizontal>.swiper-pagination-bullets,
    .swiper-pagination-bullets.swiper-pagination-horizontal {
        bottom: 1.25rem;
        top: initial;
        left: 1.5625rem;
        width: fit-content;
    }

    .swiper-horizontal>.swiper-pagination-bullets .swiper-pagination-bullet,
    .swiper-pagination-horizontal.swiper-pagination-bullets .swiper-pagination-bullet {
        margin: 0 0.1875rem;
    }

    .swiper-pagination-bullet {
        opacity: 0.3;
        width: 0.5rem;
        height: 0.5rem;
    }

    .swiper-pagination-bullet-active {
        opacity: 0.8 !important;
        background: @ink;
    }
}

.review-swiper {
    .swiper {
        padding-bottom: 8rem;
    }

    .swiper-pagination {
        width: unset !important;
        bottom: unset !important;
        top: 39.5rem !important;
    }

    .swiper-pagination-bullet {
        width: 11px;
        height: 11px;
        background: @placeholder;
        margin: 0 5px !important;

        &:hover {
            background: @paper;
        }
    }

    .swiper-pagination-bullet-active {
        background: @paper;
    }
}
@media (max-width: 800px) {
    .review-swiper {
        .swiper {
            padding-bottom: 6rem;
        }

        .swiper-pagination {
            left: 50% !important;
            top: var(--swiper-pagination-translate-y, 0) !important;
            transform: translateX(-50%) !important;
        }

        .swiper-pagination-bullet {
            margin: 0 8px !important;
        }
    }
}

@media (max-width: 480px) {
    .review-swiper {
        .swiper {
            padding-bottom: 0;
        }
    }
}
</style>
